import { Link } from 'react-router-dom'
import Logo from '../components/Logo'
import AuthHeader from '../components/AuthHeader'

const PUBLICATIONS = [
  {
    title: "ScaLER: Scalable and Efficient Logical Error Rate Estimation for Quantum Error Correction Codes",
    authors: "John Zhuoyang Ye, Jiyuan Wang, Jens Palsberg",
    venue: "arXiv:2602.04921, 2026",
    url: "https://arxiv.org/abs/2602.04921",
    tags: ["QEC", "logical error rate", "ScaLER", "stratified sampling"],
    description: "ScaLER introduces a time-budgeted stratified sampling method for estimating logical error rates of QEC codes, achieving orders-of-magnitude speedup over Monte Carlo with comparable accuracy.",
  },
  {
    title: "Computer Science Challenges in Quantum Computing: Early Fault-Tolerance and Beyond",
    authors: "Yongshan Ding, Frederic T. Chong",
    venue: "ACM Computing Surveys, 2024",
    url: "https://arxiv.org/abs/2601.20247",
    tags: ["fault-tolerance", "compilation", "survey"],
    description: "A comprehensive survey of CS challenges in quantum computing, covering compilation, error correction, and the road to fault-tolerant quantum computing.",
  },
  {
    title: "Achieving a Quantum Smart Workforce",
    authors: "Clarice D. Aiello et al.",
    venue: "Quantum Science and Technology, IOP Publishing, 2021",
    url: "https://iopscience.iop.org/article/10.1088/2058-9565/abfa64",
    tags: ["education", "workforce", "quantum"],
    description: "Strategies and frameworks for building a quantum-ready workforce through education at multiple levels.",
  },
]

const VIDEOS = [
  {
    title: "[POPL'25] Invited Talk: Welcome to Quantum 3.0!",
    speaker: "Yongshan Ding",
    event: "POPL 2025",
    youtubeId: "nHnlMiOaRXk",
    description: "CS challenges in early fault-tolerant quantum computing — compilation, error correction, and what Quantum 3.0 means for the field.",
  },
  {
    title: "Quantum Education at Scale",
    speaker: "Frederic T. Chong",
    event: "Quantum Summer Symposium 2021",
    youtubeId: "xhXddMYYgmQ",
    description: "How to scale quantum computing education to reach a broad audience of students and professionals.",
  },
]

function PublicationsPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-qcloud-light via-white to-qcloud-bg">
      <header className="bg-white/80 backdrop-blur border-b border-qcloud-border px-6 py-3 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <Link to="/">
            <Logo size="small" />
          </Link>
          <Link to="/" className="text-xl font-bold bg-gradient-to-r from-qcloud-primary to-qcloud-secondary bg-clip-text text-transparent">
            QuantumArena
          </Link>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/" className="text-sm text-qcloud-muted hover:text-qcloud-primary transition-colors">Home</Link>
          <Link to="/arena" className="text-sm text-qcloud-muted hover:text-qcloud-primary transition-colors">Arena</Link>
          <AuthHeader />
        </div>
      </header>

      <main className="max-w-4xl mx-auto py-12 px-4">
        <h1 className="text-3xl font-bold text-qcloud-text mb-2">Publications & Talks</h1>
        <p className="text-qcloud-muted mb-10">Research and talks related to the QuantumArena platform. Participants are encouraged to watch and read.</p>

        {/* Featured Talks */}
        <section className="mb-12">
          <h2 className="text-xl font-bold text-qcloud-text mb-6">Featured Talks</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {VIDEOS.map(v => (
              <div key={v.youtubeId} className="bg-white rounded-xl border border-qcloud-border overflow-hidden">
                <div className="aspect-video">
                  <iframe
                    className="w-full h-full"
                    src={`https://www.youtube.com/embed/${v.youtubeId}`}
                    title={v.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-qcloud-text">{v.title}</h3>
                  <p className="text-sm text-qcloud-muted mt-1">{v.speaker} - {v.event}</p>
                  <p className="text-sm text-qcloud-muted mt-2">{v.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Publications */}
        <section className="mb-12">
          <h2 className="text-xl font-bold text-qcloud-text mb-6">Recommended Publications</h2>
          <div className="space-y-4">
            {PUBLICATIONS.map(p => (
              <a
                key={p.url}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block bg-white rounded-xl border border-qcloud-border p-6 hover:border-qcloud-primary/50 hover:shadow-md transition-all group"
              >
                <h3 className="font-semibold text-qcloud-text group-hover:text-qcloud-primary text-lg">{p.title}</h3>
                <p className="text-sm text-qcloud-muted mt-1">{p.authors}</p>
                <p className="text-sm font-medium text-qcloud-primary/80 mt-0.5">{p.venue}</p>
                <p className="text-sm text-qcloud-muted mt-2">{p.description}</p>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {p.tags.map(tag => (
                    <span key={tag} className="px-2 py-0.5 bg-qcloud-bg rounded text-xs text-qcloud-muted">{tag}</span>
                  ))}
                </div>
              </a>
            ))}
          </div>
        </section>

        <div className="text-center text-sm text-qcloud-muted">
          <p>More publications and tutorial videos coming soon.</p>
        </div>
      </main>
    </div>
  )
}

export default PublicationsPage
